import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController, AlertController, IonInput, LoadingController } from "@ionic/angular/lazy";
import { CartService } from "../../services/cart.service";
import { Decimal } from "decimal.js";
import { StripeTerminalReaderService } from "src/app/services/stripe-terminal-reader.service";
import { ErrorResponse } from "@stripe/terminal-js";
import { APIService } from "../../services/api.service";
import * as Sentry from "@sentry/angular";
import { StorageService } from "src/app/services/storage.service";

interface Payment {
	method: string;
	price: any;
	label: string;
	payment_service_transaction_id?: string;
	payment_service_id?: string;
	stripe_payment_intent_id?: string;
}

@Component({
    selector: "app-checkout-payment-stripe-terminal",
    templateUrl: "./checkout-payment-stripe-terminal.page.html",
    styleUrls: ["./checkout-payment-stripe-terminal.page.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class CheckoutPaymentStripeTerminalPage implements OnInit {

	cart = null;
	payment: Payment = {
		method: 'credit_card',
		price: null,
		label: "Credit card",
        payment_service_id: "BE5C31BF-FC83-9865-A57A1554B936A970" // stripe
	};
	processing = false;

    payment_amount = null;
    surcharge_rate = null;
    surcharge_price = null;
    surcharge_enabled = false;

	constructor(
		public modalController: ModalController,
		public alertController: AlertController,
		public loadingController: LoadingController,
		private cartService: CartService,
        private apiService: APIService,
        private storageService: StorageService,
		public readerService: StripeTerminalReaderService
	) {}

	ngOnInit() {
		this.cartService.get().then(cart => {
			if (cart) {
				this.cart = cart;
                this.storageService.get('order_credit_card_surcharge_rate').then((credit_card_surcharge_rate) => {
                    if (credit_card_surcharge_rate && credit_card_surcharge_rate > 0) {
                        this.surcharge_enabled = true;
                        this.surcharge_rate = Number(credit_card_surcharge_rate);
                    }
                    this.payment.price = cart['amount_due'];
                    this.payment_amount = cart['amount_due'].toFixed(2);
                    this.calculatePricing();    
                });
			}
		});
	}

    calculatePricing() {

        // ensure payment isn't over the amount due
        if(this.payment.price > this.cart.amount_due) {
            this.payment.price = this.cart.amount_due;
        }
        
        if(this.surcharge_enabled && this.surcharge_rate && this.surcharge_rate > 0) {
            try {
                this.surcharge_price = Number(new Decimal(this.payment.price).times(new Decimal(this.surcharge_rate).dividedBy(100)).toDecimalPlaces(2));
                this.payment.price = Number(new Decimal(this.payment.price).add(new Decimal(this.surcharge_price)).toDecimalPlaces(2));
            }
            catch(e) {
                // If error, invalid number
                // decimal.js recommended way to check for DecimalError
                if(e instanceof Error && /DecimalError/.test(e.message)) {
                    // So disable option to add payment
                    this.payment.price = null;
                }
            }
        } 
    }

    sanitisePaymentAmount() {

        let payment_amount = this.payment_amount.toString();
        
        // only accept numbers/decimal points
        if(/[^0-9.]/.test(payment_amount)) {
            this.payment.price = null;
        }        
        else if(payment_amount.startsWith('.')) {
            this.payment.price = null;
        }
        else if(payment_amount.endsWith('.')) {
            this.payment.price = null;
        }              
        else if(payment_amount.split('.').length > 2) {
            this.payment.price = null;
        }
        else if(!payment_amount.length) {
            this.payment.price = null;
        }
        else {
            this.payment.price = Number(new Decimal(payment_amount).toDecimalPlaces(2));
        }

        this.calculatePricing();

    }    

    toggleSurcharge() {
        this.payment.price = this.payment_amount;
        this.calculatePricing();
    }    

	async addPayment() {

        if(this.surcharge_enabled && this.surcharge_rate && this.surcharge_rate > 0) {
            this.payment['surcharge_rate'] = this.surcharge_rate;
        }

        this.processing = true;

		this.readerService
			.charge(this.payment.price, this.cart["currency"].code)
			.then(charge_res => {
				if ("error" in charge_res) {
					const { code: error_code, message: error_message } = charge_res.error;
					if (error_code === "canceled") {
						// No alert, continue
					} 
                    else if (error_code === "network_timeout") {
						this.presentAlert("Network timeout", "Card reader timed out. Please check your connection.");
						this.close();
					} 
                    else if (error_code === "reader_error") {
						if (error_message === "The POS is no longer authenticated.") {
							this.presentAlert("Card reader disconnected", "Card reader might have been hot-swapped. Please reconnect");
							this.close();
						}
                        else{
							this.presentAlert("Card reader disconnected", "Cannot communicate with your card reader. Please check your connection.");
							this.close();
                        }
					} 
                    else {
						this.presentAlert("Reading card failed", "Failed to read card. Please try again.");
					}
					this.processing = false;
					this.readerService.refreshCart();
					return;
				}
				const loading = this.loadingController.create({ message: "Processing payment" });
				loading.then(loaded => loaded.present());
				this.readerService
					.processPayment(charge_res.paymentIntent)
					.then(async (process_res: { error: any } | { paymentIntent: any }) => {
                        loading.then(loaded => loaded.dismiss());
                        let payment_successful = true;                
                        if ("error" in process_res) {
                            payment_successful = false;
                            const { type: error_type, code: error_code, decline_code } = process_res.error;
                            if (error_type === "card_error") {
                                if (error_code === "card_declined") {
                                    if (decline_code === "incorrect_pin") {
                                        this.presentAlert("Card declined", "Incorrect PIN. Please try again.");
                                    } 
                                    else {
                                        this.presentAlert("Card declined", "Stripe decline code: " + decline_code);
                                    }
                                }
                            } 
                            else if (error_type === "invalid_request_error") {
                                Sentry.captureMessage('Stripe invalid request error');
                                this.presentAlert("Request failed", "Please contact Airsquare support.");
                            } 
                            else if (error_type === "api_error") {
                                Sentry.captureMessage('Stripe API error');
                                this.presentAlert("Request failed", "Stripe may be down. Please try again soon.");
                            } 
                            else {
                                // Stripe failed to return status of payment, we explicity ask Stripe whether it succeeded                
                                Sentry.captureMessage('Stripe unknown error', {
                                    extra: {
                                        stripe_error: process_res['error'],
                                        stripe_payment_intent: charge_res['paymentIntent']
                                    }
                                });  
                                await this.apiService
                                    .getStripePaymentIntent(charge_res.paymentIntent.id)
                                    .then(payment_intent => {
                                        if(payment_intent.status === "succeeded") {
                                            payment_successful = true;
                                            process_res['paymentIntent'] = payment_intent;
                                        }
                                        else {
                                            this.presentAlert("Processing payment failed", "Payment was not successful.");
                                        }
                                    })
                                    .catch((error) => {
                                        Sentry.captureException(error, {
                                            extra: {
                                                stripe_error: process_res['error'],
                                                stripe_payment_intent: charge_res['paymentIntent']
                                            }
                                        });
                                        this.presentAlert("Processing payment failed", "Unable to retrieve status of payment.");
                                    });
                            }
                        }
                        if(payment_successful) {  
                            // NOTE: Terminal JS SDK pins Stripe-Version 2018-08-23 so processPayment returns charges.data, our API (getStripePaymentIntent fallback) returns latest_charge
                            const transaction_id = process_res["paymentIntent"].latest_charge ?? process_res["paymentIntent"].charges.data[0].id;
                            this.payment.payment_service_transaction_id = transaction_id;
                            this.payment.stripe_payment_intent_id = process_res["paymentIntent"]["id"];
                            
                            this.cartService.addPayment(this.payment).finally(() => {
                                this.processing = false;
                                this.readerService.refreshCart();
                                this.close();
                            });
                        }
                        else {
                            this.processing = false;
                            this.readerService.refreshCart();
                        }
					})
					.catch(e => {
						this.processing = false;
						this.readerService.refreshCart();
                        loading.then(loaded => loaded.dismiss());
					});
			})
			.catch(e => {
				this.processing = false;
				this.readerService.refreshCart();
			});
	}

	cancelPayment() {
		this.processing = true;
		this.readerService.cancelCharge().then(response => {
			if ("error" in response) {
				this.presentAlert("Network timeout", "Card reader timed out. Please check your connection.");
			}
			this.processing = false;
		});
	}    

	close() {
		this.modalController.dismiss();
	}

	async presentAlert(header, message) {
		const alert = await this.alertController.create({
			header: header,
			message: message,
			buttons: ["OK"],
		});
		await alert.present();
	}
}
